import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';
import { useRealtime } from '../lib/realtime';

const CashContext = createContext(null);

/**
 * Holds the current business-day / cash-register session for the tenant.
 * Auto-refreshes on real-time 'cash'/'payments' events so a cashier waiting on
 * the "day not open" screen unlocks the moment a manager opens the day.
 */
export const CashProvider = ({ children }) => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/cash/current');
      setSession(data.session);
      setTotals(data.totals);
    } catch (e) {
      setSession(null);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, user?.id]);

  useRealtime((msg) => {
    if (msg && (msg.event === 'cash' || msg.event === 'payments')) refresh();
  });

  const openDay = async (opening_cash) => {
    const { data } = await api.post('/cash/open', { opening_cash: Number(opening_cash || 0) });
    await refresh();
    return data;
  };

  // Does NOT refresh: the caller shows the summary first, then triggers refresh.
  const closeDay = async (closing_cash) => {
    const { data } = await api.post('/cash/close', { closing_cash: Number(closing_cash || 0) });
    return data;
  };

  return (
    <CashContext.Provider value={{ session, totals, loading, refresh, openDay, closeDay }}>
      {children}
    </CashContext.Provider>
  );
};

export const useCash = () => useContext(CashContext);
